import React, { useState } from 'react'
import { useAuth } from './AuthContext'
import { isSupabaseConfigured } from '../../config/env'
import { AlertCircle, Lock, Mail, ArrowRight } from 'lucide-react'

interface LoginViewProps {
  onSwitchToReset?: () => void
  onSwitchToInvitation?: () => void
}

export const LoginView: React.FC<LoginViewProps> = ({
  onSwitchToReset,
  onSwitchToInvitation,
}) => {
  const { signIn } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isConfigured = isSupabaseConfigured()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMsg(null)

    if (!email || !password) {
      setErrorMsg('Introduce tu correo y contraseña')
      return
    }

    setIsSubmitting(true)
    try {
      const { error } = await signIn(email.trim(), password)
      if (error) {
        const raw = error.message || ''
        if (raw.toLowerCase().includes('invalid login credentials')) {
          setErrorMsg('Credenciales inválidas. Revisa el correo y la contraseña introducidos.')
        } else if (raw.toLowerCase().includes('failed to fetch') || raw.toLowerCase().includes('network')) {
          setErrorMsg('Error de conexión con el servidor. Comprueba tu red e inténtalo de nuevo.')
        } else {
          setErrorMsg(raw || 'Error al iniciar sesión. Revisa tus credenciales.')
        }
      }
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error inesperado de comunicación con el servicio de acceso.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <img src="/branding/logo.png" alt="FEFA" className="auth-logo" />
          <h1 style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--text-main)', marginBottom: '0.25rem' }}>
            Team Spain
          </h1>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
            Selección Nacional de Flag Football
          </p>
        </div>

        {!isConfigured && (
          <div
            style={{
              padding: '0.75rem',
              backgroundColor: 'var(--status-warning-bg)',
              color: 'var(--status-warning-text)',
              border: '1px solid var(--status-warning-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.8rem',
              marginBottom: '1.25rem',
              display: 'flex',
              gap: '0.5rem',
            }}
          >
            <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <strong>Modo de demostración local:</strong> El backend de Supabase aún no está conectado. Puedes explorar la estructura o configurar las variables de entorno para autenticarte.
            </div>
          </div>
        )}

        {errorMsg && (
          <div
            style={{
              padding: '0.75rem',
              backgroundColor: 'var(--status-danger-bg)',
              color: 'var(--status-danger-text)',
              border: '1px solid var(--status-danger-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.82rem',
              marginBottom: '1.25rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <AlertCircle size={16} style={{ flexShrink: 0 }} />
            <span>{errorMsg}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="login-email">
              Correo electrónico
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="login-email"
                type="email"
                className="form-input"
                placeholder="tu.correo@fefa.es"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
                autoComplete="email"
                required
              />
              <Mail
                size={16}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)',
                }}
              />
            </div>
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
              <label className="form-label" htmlFor="login-password" style={{ marginBottom: 0 }}>
                Contraseña
              </label>
              {onSwitchToReset && (
                <button
                  type="button"
                  onClick={onSwitchToReset}
                  style={{ fontSize: '0.75rem', color: 'var(--brand-red)', fontWeight: 500 }}
                >
                  ¿Olvidaste tu contraseña?
                </button>
              )}
            </div>
            <div style={{ position: 'relative' }}>
              <input
                id="login-password"
                type="password"
                className="form-input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
                autoComplete="current-password"
                required
              />
              <Lock
                size={16}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)',
                }}
              />
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%', marginTop: '0.5rem' }}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span>Accediendo...</span>
            ) : (
              <>
                <span>Iniciar sesión</span>
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </form>

        {onSwitchToInvitation && (
          <div style={{ marginTop: '1.5rem', textAlign: 'center', borderTop: '1px solid var(--surface-border)', paddingTop: '1rem' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              ¿Tienes un enlace de invitación?{' '}
            </span>
            <button
              type="button"
              onClick={onSwitchToInvitation}
              style={{ fontSize: '0.8rem', color: 'var(--brand-red)', fontWeight: 600 }}
            >
              Activar invitación
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
